import { useState } from "react";
import { ArrowLeft, Download, FileText, HelpCircle, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";

export default function HelpPage() {
  const { toast } = useToast();
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const response = await fetch("/api/manual", { credentials: "include" });
      if (!response.ok) {
        let message = "Bedienungsanleitung konnte nicht geladen werden.";
        try {
          const data = await response.json();
          if (data?.message) message = data.message;
        } catch {
          // body was not JSON, fallback message wird verwendet
        }
        throw new Error(message);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "FassadenCheck-Benutzerhandbuch.docx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast({
        title: "Download gestartet",
        description: "Die Bedienungsanleitung wird heruntergeladen.",
      });
    } catch (error) {
      toast({
        title: "Download fehlgeschlagen",
        description:
          error instanceof Error
            ? error.message
            : "Bitte später erneut versuchen.",
        variant: "destructive",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-3xl mx-auto">
        <div className="mb-2">
          <Link href="/dashboard">
            <Button variant="ghost" size="sm" className="text-gray-500 hover:text-bauwerk-teal -ml-2">
              <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
            </Button>
          </Link>
        </div>
        <div className="mb-8 flex items-start gap-3">
          <HelpCircle
            className="w-8 h-8 text-bauwerk-teal flex-shrink-0 mt-1"
            aria-hidden="true"
          />
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Hilfe &amp; Bedienungsanleitung
            </h1>
            <p className="text-gray-600">
              Das ausführliche Benutzerhandbuch begleitet Sie Schritt für
              Schritt durch FassadenCheck — vom ersten Foto auf der Baustelle
              bis zum fertigen Angebot.
            </p>
          </div>
        </div>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText
                className="w-5 h-5 text-bauwerk-teal"
                aria-hidden="true"
              />
              Bedienungsanleitung als Word-Dokument
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 mb-4">
              Das Handbuch ist ein Word-Dokument (.docx) mit Inhaltsverzeichnis,
              Screenshots und Hinweisen. Sie können es lokal speichern, drucken
              oder im Team weitergeben.
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-6 space-y-1">
              <li>Erste Schritte und Anmeldung</li>
              <li>Vor-Ort-Erfassung mit dem Handy (Fotos, Notizen, Diktat)</li>
              <li>KI-Bildauswertung</li>
              <li>Leistungsverzeichnis pflegen</li>
              <li>Angebotserstellung mit Pauschalen, Plausibilitätsprüfung und PDF-Export</li>
              <li>Tipps &amp; häufige Fragen</li>
            </ul>
            <Button
              onClick={handleDownload}
              disabled={isDownloading}
              className="bg-bauwerk-teal hover:bg-bauwerk-teal/90 text-white"
              data-testid="button-download-manual"
            >
              <Download className="w-4 h-4 mr-2" aria-hidden="true" />
              {isDownloading
                ? "Download läuft …"
                : "Bedienungsanleitung herunterladen"}
            </Button>
            <p className="text-sm text-gray-500 mt-3">
              Tipp: In Word einmal mit Rechtsklick aufs Inhaltsverzeichnis
              gehen und „Felder aktualisieren" wählen — dann passen sich die
              Seitenzahlen an.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail
                className="w-5 h-5 text-bauwerk-teal"
                aria-hidden="true"
              />
              Sie kommen nicht weiter?
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700">
              Wenn Sie eine Frage haben, die das Handbuch nicht beantwortet,
              wenden Sie sich bitte an Ihre Ansprechperson bei der Hofer GesmbH (Hofer Seeboden).
              Wir helfen gerne weiter.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
